// MACHINERY V2 (Stroje) — horizontal chip row with a right-edge fade (M2.16a).
//
// >>> THIS IS A STANDARD, NOT A STYLE CHOICE. <<< mobile/README.md §Standards:
// "Chip rows scroll horizontally with a right-edge fade affordance (30px
// gradient) — never wrap, never clip without the fade." A row that clips without
// the fade reads as a complete list, so the user never learns there is more.
//
// GENERIC ON PURPOSE — three consumers are already scheduled: this step's
// active-filter chips, 16b's V3 sort-chip row (ZORADIT · Stroj / Stav / Otv.
// SR), 16c's katalog category tabs and 16d's profile tab bar. Building it per
// surface is four fades to keep in sync, and the profile's is the one the
// README explicitly draws with it.
//
// INSIDE A SHEET, WRAPPING IS FINE AND THIS IS NOT USED: the design's own sheet
// wraps its group-by chips (machinery-mobile.jsx:147, `flexWrap: "wrap"`). The
// standard above is about chip rows on a PAGE, where horizontal space is the
// scarce axis and a wrapped row would push the fleet down the screen.
//
// THE FADE IS PERMANENT, not scroll-position-aware. The design's own row renders
// it unconditionally (machinery-mobile.jsx:69), and the alternative costs a
// ResizeObserver plus a scroll listener per row to hide a 30px gradient that is
// harmless when there is nothing to scroll.
//
// >>> ...BUT IT MUST NEVER COVER THE LAST CONTROL. <<< A permanent gradient at
// a fixed offset from the right edge sat directly over whatever ended there —
// and in the fleet's row that is the terminal "Zrusit" clear-all action
// (ActiveFilterChips.tsx), which rendered half-erased. The fade's job is to say
// "the row continues"; saying it over an ACTION says instead that the action is
// disabled or clipped. Fixed by a trailing SPACER exactly one fade wide, so the
// last child can always be scrolled fully clear of the gradient and, at rest,
// the gradient has its own blank strip to fall on. Chosen over exempting the
// last child (which would need the consumer to declare which child that is, in
// four surfaces) and over `paddingRight` (which scroll containers have a
// history of dropping at the scroll end).
//
// The hidden scrollbar is `.mv2-chiprow` in machinery-v2-mobile.scss — the token
// sheet paints an 8px thumb on every scrollable descendant, which on a 26px chip
// row is taller than the gap it sits in.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

export interface ChipScrollerProps {
  /** The colour the fade resolves TO — i.e. whatever the row sits on. Page
   *  surfaces are var(--bg); a row inside a sheet or card is var(--card). Wrong
   *  value = a visible grey smear rather than a fade. */
  fadeTo?: string;
  gap?: number;
  /** Horizontal page padding. Applied INSIDE the scroller so the first chip
   *  starts at the page margin and the last one can scroll clear of it. */
  paddingInline?: number;
  /** Vertical padding. The row's own height comes from this plus the tallest
   *  child — the fleet's chips are 26px pills inside 44px targets. */
  paddingBlock?: number;
  /**
   * ARIA role for the SCROLL CONTAINER — the element the children are direct
   * children of (M2.16d).
   *
   * >>> IT EXISTS BECAUSE THIS COMPONENT SITS BETWEEN A CONTAINER ROLE AND ITS
   * CHILD ROLES. <<< The profile tab bar is `role="tablist"` holding
   * `role="tab"` buttons, and ARIA requires tabs to be OWNED by the tablist.
   * With the role on a wrapper outside this component, two generic divs sit in
   * between and the ownership is broken — the tablist reports zero tabs. Passing
   * the role IN puts it on the element that really is the parent.
   *
   * Omitted, nothing is emitted and every existing consumer (the fleet's active
   * filter chips, V3's sort chips, the katalog's category tabs) renders exactly
   * as before. The trailing fade spacer below is `aria-hidden`, so it is not
   * exposed as a child of whatever role is applied here.
   */
  role?: string;
  /** Accessible name for `role`. Ignored when `role` is not set. */
  ariaLabel?: string;
  children: React.ReactNode;
}

/** Width of the right-edge gradient AND of the trailing spacer that keeps the
 *  last control out from under it. ONE constant, because the moment the two
 *  disagree the bug this file documents comes back. */
const FADE_WIDTH = 30;

const ChipScroller: React.FC<ChipScrollerProps> = ({
  fadeTo = "var(--bg)",
  gap = 6,
  paddingInline = 14,
  paddingBlock = 0,
  role,
  ariaLabel,
  children,
}) => (
  <div style={{ position: "relative", flexShrink: 0, minWidth: 0 }}>
    <div
      className="mv2-chiprow"
      role={role}
      aria-label={role ? ariaLabel : undefined}
      style={{
        display: "flex",
        alignItems: "center",
        gap,
        padding: `${paddingBlock}px ${paddingInline}px`,
        overflowX: "auto",
        // NEVER wrap — see the header. `nowrap` plus each child's own
        // flexShrink:0 is what makes the row scroll instead of squashing.
        flexWrap: "nowrap",
        WebkitOverflowScrolling: "touch",
      }}
    >
      {children}
      {/* The fade's landing strip — see the header. Zero-height so it can never
          affect the row's own height, `flexShrink: 0` so it survives a full
          row. */}
      <span aria-hidden style={{ width: FADE_WIDTH, height: 0, flexShrink: 0 }} />
    </div>
    <span
      aria-hidden
      style={{
        position: "absolute",
        right: 0,
        top: 0,
        bottom: 0,
        width: FADE_WIDTH,
        background: `linear-gradient(90deg, transparent, ${fadeTo})`,
        pointerEvents: "none",
      }}
    />
  </div>
);

export default ChipScroller;
